'use client';

import { Mail } from 'lucide-react';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useProfileStore } from '@/store/profile.store';
import {
  EMAIL_GATE_DESCRIPTION,
  EMAIL_VERIFICATION_COPY,
  PROFILE_EMAIL_SECTION_ID,
} from '../../consts';
import { useEmailVerificationGateStore } from '../../store';
import { resolveEmailPublishGate } from '../../utils';

export const EmailVerificationDialog = () => {
  const router = useRouter();
  const isOpen = useEmailVerificationGateStore((state) => state.isOpen);
  const action = useEmailVerificationGateStore((state) => state.action);
  const dismissGate = useEmailVerificationGateStore((state) => state.dismissGate);
  const heldAction = useEmailVerificationGateStore((state) => state.heldAction);
  const openGate = useEmailVerificationGateStore((state) => state.openGate);
  const clearHeldGate = useEmailVerificationGateStore((state) => state.clearHeldGate);
  const verificationStatus = useProfileStore((state) => state.userData?.verificationStatus);
  const isProfileLoading = useProfileStore((state) => state.isLoadingUser || state.isFetching);
  const hasProfile = useProfileStore((state) => state.userData !== null);
  const profileError = useProfileStore((state) => state.error);
  const description = action
    ? EMAIL_GATE_DESCRIPTION[action]
    : EMAIL_VERIFICATION_COPY.gateFallback;

  useEffect(() => {
    if (!heldAction) return;
    const decision = resolveEmailPublishGate(
      verificationStatus,
      isProfileLoading,
      hasProfile,
      profileError,
    );
    if (decision === 'pending') return;
    if (decision === 'allow') {
      clearHeldGate();
      return;
    }
    openGate(heldAction);
  }, [
    clearHeldGate,
    hasProfile,
    heldAction,
    isProfileLoading,
    openGate,
    profileError,
    verificationStatus,
  ]);

  const handleGoToProfile = () => {
    dismissGate();
    router.push(`/profile/edit#${PROFILE_EMAIL_SECTION_ID}`);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) dismissGate();
      }}
    >
      <DialogContent
        className="max-w-[470px] gap-0 rounded-[18px] border-0 bg-white p-7 text-center shadow-[0_24px_90px_rgba(0,0,0,0.3)] dark:bg-[#2a2e36] sm:rounded-[18px]"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-[17px] bg-[#eef8d7] text-[#485e1d]">
          <Mail className="h-6 w-6" aria-hidden="true" />
        </div>
        <DialogTitle className="text-[25px] font-semibold leading-tight text-[#1B1D22] dark:text-white">
          {EMAIL_VERIFICATION_COPY.gateTitle}
        </DialogTitle>
        <DialogDescription className="mt-2 text-sm leading-relaxed text-[#676a72] dark:text-[#a0a3ae]">
          {description}
        </DialogDescription>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={dismissGate}
            className="min-h-11 w-full rounded-[10px] bg-[#eceef2] px-4 text-sm font-bold text-[#33363c] hover:bg-[#e2e4ea] sm:w-auto"
          >
            {EMAIL_VERIFICATION_COPY.notNow}
          </button>
          <button
            type="button"
            onClick={handleGoToProfile}
            className="min-h-11 w-full rounded-[10px] bg-[#A9F84A] px-4 text-sm font-bold text-[#1b1d22] hover:bg-[#8EE328] sm:w-auto"
          >
            {EMAIL_VERIFICATION_COPY.goToProfile}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
